import { Flame, Droplet, Leaf, Sparkles, Zap, Snowflake } from "lucide-react";

const INGREDIENTS = [
    { n: "01", name: "Filtrovaná voda", note: "Základ všetkého" },
    { n: "02", name: "Trstinový cukor", note: "Surový, nerafinovaný" },
    { n: "03", name: "Citrusová kôra", note: "Ručne lúpaná" },
    { n: "04", name: "Tajné korenie", note: "Nepýtaj sa" },
    { n: "05", name: "Studený extrakt", note: "Pomaly lúhovaný" },
    { n: "06", name: "Sýtená sóda", note: "Pre ten kop" },
];

const FLAVOR_NOTES = [
    {
        key: "pikantna",
        icon: Flame,
        label: "Pikantná",
        intensity: "Vysoká",
        desc: "Hrejivý dozvuk na jazyku",
    },
    {
        key: "citrus",
        icon: Droplet,
        label: "Citrusová",
        intensity: "Stredná",
        desc: "Žiarivá, ostrá, živá",
    },
    {
        key: "bylinkova",
        icon: Leaf,
        label: "Bylinková",
        intensity: "Nízka",
        desc: "Šepot, nie krik",
    },
    {
        key: "sumiva",
        icon: Sparkles,
        label: "Šumivá",
        intensity: "Vysoká",
        desc: "Sýti, akoby bola nahnevaná",
    },
    {
        key: "razantna",
        icon: Zap,
        label: "Razantná",
        intensity: "Veľmi vysoká",
        desc: "Udrie prvá, pýta sa potom",
    },
    {
        key: "chladny-dozvuk",
        icon: Snowflake,
        label: "Chladný dozvuk",
        intensity: "Stredná",
        desc: "Prekvapivo čistý",
    },
];

export const ProductDetails = () => {
    return (
        <section
            id="details"
            data-testid="details-section"
            className="relative px-6 sm:px-10 py-24 sm:py-32 border-t border-white/10"
        >
            <div className="max-w-[1400px] mx-auto">
                <div className="grid grid-cols-12 gap-10 mb-16">
                    <div className="col-span-12 lg:col-span-3">
                        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D91C1C]">
                            02 / Čo je vnútri
                        </div>
                    </div>
                    <div className="col-span-12 lg:col-span-9">
                        <h2
                            data-testid="details-heading"
                            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9] text-white"
                        >
                            Suroviny{" "}
                            <span className="text-white/40">&amp; chuť</span>
                        </h2>
                    </div>
                </div>

                {/* Bento Grid */}
                <div className="grid grid-cols-12 gap-4 sm:gap-5">
                    {/* Big ingredients block */}
                    <div
                        data-testid="ingredients-block"
                        className="col-span-12 lg:col-span-7 border border-white/10 bg-[#0A0A0A] p-8 sm:p-12"
                    >
                        <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">
                            Recept (skoro celý)
                        </div>
                        <ul className="divide-y divide-white/10">
                            {INGREDIENTS.map((item) => (
                                <li
                                    key={item.n}
                                    data-testid={`ingredient-${item.n}`}
                                    className="flex items-baseline gap-6 py-4 group cursor-default"
                                >
                                    <span className="font-display text-xl text-[#D91C1C] w-10 shrink-0">
                                        {item.n}
                                    </span>
                                    <span className="font-display uppercase text-2xl sm:text-3xl tracking-tight text-white group-hover:translate-x-1 transition-transform">
                                        {item.name}
                                    </span>
                                    <span className="ml-auto text-xs uppercase tracking-[0.2em] text-white/40 hidden sm:block">
                                        {item.note}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Stat card 1 */}
                    <div
                        data-testid="stat-card-batch"
                        className="col-span-6 lg:col-span-5 border border-white/10 bg-[#D91C1C] p-8 sm:p-10 flex flex-col justify-between min-h-[180px]"
                    >
                        <div className="text-[11px] uppercase tracking-[0.3em] text-white/80">
                            Veľkosť série
                        </div>
                        <div>
                            <div className="font-display text-6xl sm:text-7xl text-white leading-none">
                                12
                            </div>
                            <div className="text-sm uppercase tracking-[0.2em] text-white/80 mt-2">
                                Fliaš na sériu
                            </div>
                        </div>
                    </div>

                    {/* Stat card 2 */}
                    <div
                        data-testid="stat-card-volume"
                        className="col-span-6 lg:col-span-2 border border-white/10 bg-[#0A0A0A] p-6 flex flex-col justify-between min-h-[180px]"
                    >
                        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                            Obj
                        </div>
                        <div>
                            <div className="font-display text-4xl text-white leading-none">
                                500
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                                ML
                            </div>
                        </div>
                    </div>

                    {/* Flavor profile grid */}
                    <div
                        data-testid="flavor-profile-block"
                        className="col-span-12 border border-white/10 bg-[#0A0A0A] p-8 sm:p-12"
                    >
                        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-4">
                            <h3
                                data-testid="flavor-heading"
                                className="font-display uppercase text-3xl sm:text-4xl tracking-tighter text-white"
                            >
                                Chuťový profil
                            </h3>
                            <div className="text-[11px] uppercase tracking-[0.3em] text-white/40">
                                Ako to chutí
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10">
                            {FLAVOR_NOTES.map((note) => {
                                const Icon = note.icon;
                                return (
                                    <div
                                        key={note.key}
                                        data-testid={`flavor-${note.key}`}
                                        className="bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 sm:p-8 group"
                                    >
                                        <div className="flex items-start justify-between mb-6">
                                            <Icon
                                                size={28}
                                                className="text-[#D91C1C] group-hover:scale-110 transition-transform"
                                                strokeWidth={1.5}
                                            />
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 border border-white/15 px-2 py-1">
                                                {note.intensity}
                                            </span>
                                        </div>
                                        <div className="font-display uppercase text-2xl tracking-tighter text-white">
                                            {note.label}
                                        </div>
                                        <div className="text-sm text-white/50 mt-2">
                                            {note.desc}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
